import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/library';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import '../styles/TestScanner.css';

const FORMAT_NAMES = {
  0: 'AZTEC',
  1: 'CODABAR',
  2: 'CODE_39',
  3: 'CODE_93',
  4: 'CODE_128',
  5: 'DATA_MATRIX',
  6: 'EAN_8',
  7: 'EAN_13',
  8: 'ITF',
  9: 'MAXICODE',
  10: 'PDF_417',
  11: 'QR_CODE',
  12: 'RSS_14',
  13: 'RSS_EXPANDED',
  14: 'UPC_A',
  15: 'UPC_E',
  16: 'UPC_EAN',
};

const TestScanner = () => {
  const [scanHistory, setScanHistory] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const videoRef = useRef(null);
  const lastScanRef = useRef('');
  const navigate = useNavigate();
  const { token, userRole } = useAuth();
  const backPath = userRole === 'picker' ? '/picking' : '/init';

  const decodeBatchQr = async (text, entryId) => {
    try {
      const response = await fetch(`${API_BASE}/decode-batch-qr/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify({ text }),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (!data.valid) return;
      const [yyyy, mm, dd] = (data.exp_date || '').split('-');
      const expDate = yyyy && mm && dd ? `${dd}-${mm}-${yyyy}` : data.exp_date;
      setScanHistory(prev =>
        prev.map(item =>
          item.id === entryId
            ? { ...item, decoded: { sku: data.sku, expDate } }
            : item,
        ),
      );
    } catch (err) {
      console.error('Error decoding batch QR:', err);
    }
  };

  useEffect(() => {
    const codeReader = new BrowserMultiFormatReader();
    let activeStream;

    const initializeScanner = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });

        activeStream = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }

        codeReader.decodeFromVideoDevice(null, videoRef.current, (result) => {
          if (result) {
            const text = result.getText();
            const format = result.getBarcodeFormat();

            if (text === lastScanRef.current) return;
            lastScanRef.current = text;

            setTimeout(() => {
              lastScanRef.current = '';
            }, 2000);

            const entryId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            setScanHistory(prev => [
              {
                id: entryId,
                text,
                format: FORMAT_NAMES[format] || `FORMAT_${format}`,
                timestamp: new Date().toLocaleTimeString(),
                decoded: null,
              },
              ...prev,
            ]);

            if (FORMAT_NAMES[format] === 'QR_CODE') {
              decodeBatchQr(text, entryId);
            }
          }
        });
      } catch (error) {
        console.error('Error initializing scanner:', error);
        setErrorMessage('No se pudo acceder a la cámara. Revisa los permisos.');
      }
    };

    initializeScanner();

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach((track) => track.stop());
      }
      codeReader.reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="test-scanner-page">
      <button className="test-scanner-back" onClick={() => navigate(backPath)}>←</button>
      <h1 className="test-scanner-title">Test Scanner</h1>
      <div className="test-scanner-container">
        <video ref={videoRef} className="test-scanner-video" autoPlay playsInline></video>
        <div className="test-scanner-overlay">
          <p className="test-scanner-instructions">Escanea cualquier código para ver su contenido</p>
        </div>
      </div>
      {errorMessage && <p className="test-scanner-error">{errorMessage}</p>}
      <div className="test-scanner-history">
        {scanHistory.length === 0 && (
          <p className="test-scanner-empty">Sin escaneos aún</p>
        )}
        {scanHistory.map((scan) => (
          <div key={scan.id} className="test-scanner-item">
            <div className="test-scanner-item-header">
              <span className="test-scanner-format">{scan.format}</span>
              <span className="test-scanner-time">{scan.timestamp}</span>
            </div>
            <p className="test-scanner-text">{scan.text}</p>
            {scan.decoded && (
              <div className="test-scanner-decoded">
                <p className="test-scanner-decoded-row">
                  <span className="test-scanner-decoded-label">SKU:</span>
                  <span className="test-scanner-decoded-value">{scan.decoded.sku}</span>
                </p>
                <p className="test-scanner-decoded-row">
                  <span className="test-scanner-decoded-label">Vencimiento:</span>
                  <span className="test-scanner-decoded-value">{scan.decoded.expDate}</span>
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TestScanner;
